// Tiny fetch wrapper for the People API.
// In dev, vite proxies /api -> :3019; in prod, nginx routes /people/api/* -> :3019.
// Tenant-scoped endpoints live under /api/v1/t/:slug/*; we expose `apiTenant` for those.

const API_BASE = import.meta.env.PROD ? "/people/api" : "/api"

import { isEmbedded, notifyAuthRequired } from "./use-embed"

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem("people.token")
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  })
  // 401 from a protected endpoint means the stored token is gone or expired.
  // Standalone: wipe credentials and bounce to /login.
  // Embedded:   notify the host so it can re-mint a token; don't redirect.
  if (res.status === 401 && token) {
    localStorage.removeItem("people.token")
    localStorage.removeItem("people.user")
    if (isEmbedded()) {
      notifyAuthRequired()
    } else if (!location.pathname.endsWith("/login")) {
      location.href = (import.meta.env.PROD ? "/people" : "") + "/login"
    }
  }
  const text = await res.text()
  const body = text ? JSON.parse(text) : null
  if (!res.ok) {
    throw new ApiError(res.status, body?.error ?? `http_${res.status}`, body)
  }
  return body as T
}

export const api = request

// Helper for tenant-scoped routes — saves repeating /v1/t/:slug everywhere.
export function apiTenant<T = unknown>(slug: string, path: string, init?: RequestInit): Promise<T> {
  return request<T>(`/v1/t/${slug}${path}`, init)
}

export interface HealthResponse {
  ok: boolean
  service: string
  version: string
  nodeEnv: string
  ruleSet: string
  startedAt: string
  uptimeSeconds: number
}
